import { AfterViewInit, Component, ElementRef, HostListener, Inject, OnDestroy, OnInit, PLATFORM_ID, ViewChild } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { Subscription, forkJoin } from 'rxjs';
import { Footer } from '../../layout/footer/footer.component';
import { NormalNavbar } from '../../layout/normal-navbar/normal-navbar';
import {
  BlogApiService,
  BlogEntity,
  BlogListResponse,
} from '../../core/services/blog/blog-api.service';
import { SeoService } from '../../core/services/seo/seo.service';

@Component({
  selector: 'app-blog',
  standalone: true,
  imports: [CommonModule, FormsModule, NormalNavbar, Footer],
  templateUrl: './blog.html',
  styleUrl: './blog.scss',
})
export class Blog implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('scrollSentinel') scrollSentinel?: ElementRef<HTMLDivElement>;
  private observer?: IntersectionObserver;

  private readonly subscriptions = new Subscription();
  private readonly pageSize = 10;

  blogs: BlogEntity[] = [];
  featuredBlogs: BlogEntity[] = [];
  relatedBlogs: BlogEntity[] = [];
  tags: string[] = [];

  loading = false;
  loadingMore = false;
  errorMessage = '';

  viewMode: 'grid' | 'list' = 'grid';

  page = 1;
  perPage = this.pageSize;
  total = 0;
  totalPages = 1;

  filters = {
    search: '',
    category: '',
    tag: '',
    featured: '',
    sort_by: 'published_at',
  };

  readonly sortOptions = [
    { label: 'Most Recent', value: 'published_at' },
    { label: 'Most Viewed', value: 'views' },
    { label: 'Most Liked', value: 'likes' },
    { label: 'Recently Created', value: 'created_at' },
  ];

  constructor(
    private readonly blogApi: BlogApiService,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly seoService: SeoService,
    @Inject(PLATFORM_ID) private readonly platformId: Object,
  ) {}

  ngOnInit(): void {
    this.seoService.setSeo({
      title: 'Cinema Journal & Film Analysis',
      description: 'Explore in-depth movie essays, behind-the-scenes cinema analysis, director retrospectives, and editorial features on The Scenetale.',
      url: '/blogs',
      type: 'blog'
    });

    this.preloadCollections();

    this.subscriptions.add(
      this.route.queryParamMap.subscribe((queryMap) => {
        this.page = 1;
        this.perPage = this.pageSize;
        this.filters.search = queryMap.get('search') ?? '';
        this.filters.category = queryMap.get('category') ?? '';
        this.filters.tag = queryMap.get('tag') ?? '';
        this.filters.featured = queryMap.get('featured') ?? '';

        const sortBy = queryMap.get('sort_by') ?? 'published_at';
        this.filters.sort_by = this.sortOptions.some((opt) => opt.value === sortBy)
          ? sortBy
          : 'published_at';

        this.loadBlogs(1);
      }),
    );
  }

  ngAfterViewInit(): void {
    if (isPlatformBrowser(this.platformId)) {
      this.initIntersectionObserver();
    }
  }

  ngOnDestroy(): void {
    this.observer?.disconnect();
    this.subscriptions.unsubscribe();
  }

  @HostListener('window:scroll')
  onWindowScroll(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    if (this.loading || this.loadingMore) return;
    if (this.totalPages > 1 && this.page >= this.totalPages) return;

    const scrollTop = window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0;
    const clientHeight = window.innerHeight || document.documentElement.clientHeight || document.body.clientHeight || 0;
    const scrollHeight = Math.max(
      document.documentElement.scrollHeight,
      document.body.scrollHeight,
      document.documentElement.offsetHeight || 0,
      document.body.offsetHeight || 0
    );

    if (scrollTop + clientHeight >= scrollHeight - 800) {
      this.loadMore();
    }
  }

  private initIntersectionObserver(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    const sentinel = this.scrollSentinel?.nativeElement;
    if (!sentinel) return;

    this.observer?.disconnect();
    this.observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry && entry.isIntersecting) {
          this.loadMore();
        }
      },
      {
        root: null,
        rootMargin: '600px',
        threshold: 0,
      }
    );

    this.observer.observe(sentinel);
  }

  loadMore(): void {
    if (this.loading || this.loadingMore) return;
    if (this.page >= this.totalPages) return;
    this.loadBlogs(this.page + 1);
  }

  applyFilters(): void {
    this.router.navigate(['/blogs'], {
      queryParams: {
        search: this.filters.search || null,
        category: this.filters.category || null,
        tag: this.filters.tag || null,
        featured: this.filters.featured || null,
        sort_by: this.filters.sort_by,
      },
    });
  }

  clearFilters(): void {
    this.filters = {
      search: '',
      category: '',
      tag: '',
      featured: '',
      sort_by: 'published_at',
    };
    this.router.navigate(['/blogs']);
  }

  setTag(tag: string): void {
    this.filters.tag = tag;
    this.applyFilters();
  }

  openBlog(blog: BlogEntity): void {
    const slug = this.blogSlug(blog);
    if (!slug) {
      return;
    }
    this.router.navigate(['/blog', slug]);
  }

  setViewMode(mode: 'grid' | 'list'): void {
    this.viewMode = mode;
  }

  readTime(blog: BlogEntity | null): number {
    const minutes = (blog as any)?.read_time_minutes;
    if (typeof minutes === 'number' && minutes > 0) return minutes;
    const parsed = Number(minutes);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 4;
  }

  authorName(blog: BlogEntity | null): string {
    return this.toString((blog as any)?.author_name, 'Cinema Desk');
  }

  onImageError(event: Event): void {
    const target = event.target as HTMLImageElement;
    if (target && !target.src.includes('backdrop-image.jpg')) {
      target.src = '/assets/images/backdrop-image.jpg';
    }
  }

  sectionCount(blog: BlogEntity | null): number {
    if (!blog) return 0;
    const sections = (blog as any).sections;
    if (Array.isArray(sections)) return sections.length;
    const sc = (blog as any).section_count;
    return typeof sc === 'number' ? sc : 0;
  }

  trackByBlog = (index: number, blog: BlogEntity): string => {
    return this.getBlogId(blog) || this.blogSlug(blog) || `blog-${index}`;
  }
  
  getBlogId(blog: BlogEntity | null): string {
    if (!blog) {
      return '';
    }
    const value = blog.id ?? blog._id;
    return typeof value === 'string' ? value : '';
  }

  blogSlug(blog: BlogEntity | null): string {
    if (!blog) {
      return '';
    }
    return typeof blog.slug === 'string' ? blog.slug : '';
  }

  blogTitle(blog: BlogEntity | null): string {
    return this.toString(blog?.title, 'Untitled Blog');
  }

  blogSummary(blog: BlogEntity | null): string {
    const fallbackExcerpt = typeof blog?.excerpt === 'string' ? blog.excerpt : '';
    return this.toString(blog?.summary, this.toString(fallbackExcerpt, 'No summary available yet.'));
  }

  blogCategory(blog: BlogEntity | null): string {
    return this.toString(blog?.category, 'General');
  }

  blogTags(blog: BlogEntity | null): string[] {
    if (!blog || !Array.isArray(blog.tags)) {
      return [];
    }
    return blog.tags.filter((tag): tag is string => typeof tag === 'string' && tag.trim().length > 0);
  }

  blogImage(blog: BlogEntity | null): string {
    const candidates = [
      blog?.thumbnail_image,
      blog?.backdrop_image,
      blog?.image_url,
      blog?.cover_image,
      blog?.['featured_image_url'],
    ];
    const found = candidates.find((item) => typeof item === 'string' && item.trim().length > 0);
    return typeof found === 'string' ? found : '/assets/images/backdrop-image.jpg';
  }

  blogPublishedAt(blog: BlogEntity | null): string {
    const date = this.toDate(blog?.published_at) ?? this.toDate(blog?.created_at);
    return date
      ? date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
      : 'Draft';
  }

  private preloadCollections(): void {
    this.subscriptions.add(
      forkJoin({
        featured: this.blogApi.getFeatured(5),
        tags: this.blogApi.getTags(),
      }).subscribe({
        next: ({ featured, tags }) => {
          this.featuredBlogs = this.normalizeBlogArray(featured);
          this.tags = this.normalizeTagArray(tags);
        },
        error: () => {
          this.featuredBlogs = [];
          this.tags = [];
        },
      }),
    );
  }

  private loadBlogs(pageToLoad: number = 1): void {
    if (pageToLoad === 1) {
      this.loading = true;
      this.errorMessage = '';
    } else {
      this.loadingMore = true;
    }

    const featuredFilter = this.filters.featured === '' ? undefined : this.filters.featured === 'true';

    this.subscriptions.add(
      this.blogApi
        .listBlogs({
          page: pageToLoad,
          per_page: this.pageSize,
          category: this.filters.category || undefined,
          tag: this.filters.tag || undefined,
          featured: featuredFilter,
          search: this.filters.search || undefined,
          sort_by: this.filters.sort_by as 'published_at' | 'views' | 'likes' | 'created_at',
        })
        .subscribe({
          next: (res) => {
            const normalized = this.normalizeListResponse(res);
            if (pageToLoad === 1) {
              this.blogs = normalized.items;
            } else {
              const existingIds = new Set(
                this.blogs.map((b) => this.getBlogId(b) || this.blogSlug(b)).filter(Boolean)
              );
              const newItems = normalized.items.filter((b) => {
                const key = this.getBlogId(b) || this.blogSlug(b);
                return key ? !existingIds.has(key) : true;
              });
              this.blogs = [...this.blogs, ...newItems];
            }

            this.page = normalized.page > 0 ? normalized.page : pageToLoad;
            this.perPage = this.pageSize;
            this.total = normalized.total;
            this.totalPages = normalized.total_pages;
            this.loading = false;
            this.loadingMore = false;

            if (isPlatformBrowser(this.platformId)) {
              setTimeout(() => this.initIntersectionObserver(), 150);
            }
          },
          error: () => {
            this.loading = false;
            this.loadingMore = false;
            if (pageToLoad === 1) {
              this.blogs = [];
              this.errorMessage = 'Failed to fetch blogs. Check API availability and try again.';
            }
          },
        }),
    );
  }

  private normalizeListResponse(res: BlogListResponse): {
    items: BlogEntity[];
    page: number;
    per_page: number;
    total: number;
    total_pages: number;
  } {
    const dataPayload = typeof res.data === 'object' && res.data !== null ? res.data : undefined;
    const dataAsRecord = dataPayload && !Array.isArray(dataPayload)
      ? (dataPayload as { items?: BlogEntity[]; blogs?: BlogEntity[]; results?: BlogEntity[] })
      : undefined;

    const items = this.normalizeBlogArray(
      (Array.isArray(res.data) ? res.data : undefined) ??
      res.items ??
      res.blogs ??
      res.results ??
      dataAsRecord?.items ??
      dataAsRecord?.blogs ??
      dataAsRecord?.results,
    );
    const page = this.toPositiveInt(res.page, this.page);
    const perPage = this.pageSize;
    const total = this.toNonNegativeInt(res.total ?? res.total_items, items.length);
    const totalPages = this.toPositiveInt(res.total_pages ?? res.pages, Math.max(1, Math.ceil(total / perPage)));

    return {
      items,
      page,
      per_page: perPage,
      total,
      total_pages: totalPages,
    };
  }

  private normalizeBlogArray(value: unknown): BlogEntity[] {
    if (!Array.isArray(value)) {
      return [];
    }
    return value.filter((item): item is BlogEntity => typeof item === 'object' && item !== null);
  }

  private normalizeTagArray(value: unknown): string[] {
    if (!Array.isArray(value)) {
      return [];
    }
    return value.filter((tag): tag is string => typeof tag === 'string' && tag.trim().length > 0);
  }

  private toPositiveInt(value: unknown, fallback: number): number {
    const parsed = Number(value);
    if (!Number.isFinite(parsed) || parsed < 1) {
      return fallback;
    }
    return Math.floor(parsed);
  }

  private toNonNegativeInt(value: unknown, fallback: number): number {
    const parsed = Number(value);
    if (!Number.isFinite(parsed) || parsed < 0) {
      return fallback;
    }
    return Math.floor(parsed);
  }

  private toString(value: unknown, fallback: string): string {
    return typeof value === 'string' && value.trim().length > 0 ? value : fallback;
  }

  private toDate(value: unknown): Date | null {
    if (typeof value === 'string' && value.trim().length > 0) {
      const parsed = new Date(value);
      return Number.isNaN(parsed.getTime()) ? null : parsed;
    }

    if (typeof value === 'object' && value !== null) {
      const raw = (value as { $date?: unknown }).$date;
      if (typeof raw === 'string' && raw.trim().length > 0) {
        const parsed = new Date(raw);
        return Number.isNaN(parsed.getTime()) ? null : parsed;
      }
    }

    return null;
  }
}
